const HERO_IMAGE =
  'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?auto=format&fit=crop&w=1600&q=80';

const DIAGRAM_LEGEND = [
  { color: '#fbbf24', label: 'GK — Goalkeeper' },
  { color: '#3b82f6', label: 'DEF — Your defenders' },
  { color: '#22c55e', label: 'MID — Midfielders' },
  { color: '#ef4444', label: 'FWD — Striker' },
  { color: '#f97316', label: 'OPP — Opponents' },
];

/** Featured guide: 7v7 defender positioning and back-line pressing when the other team attacks */
export const SEVEN_V_SEVEN_DEFENDER_PRESSING_POST = {
  id: 'featured-7v7-defender-pressing',
  slug: '7v7-defender-positioning-pressing-guide',
  title: '7v7 Defenders: Positioning & Pressing When the Other Team Attacks',
  excerpt:
    'Compact shape, one-steps-one-covers, wing shifts, and press triggers for the two center backs in 7v7—with animated diagrams and free YouTube coaching videos.',
  publishedAt: '2026-08-30T21:55:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'Tactics Desk',
  blogCategory: 'exercise',
  audience: '7v7 defenders',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  relatedPostsTitle: 'More 7v7 & defender guides',
  relatedPosts: [
    {
      slug: '7v7-soccer-formation-2-3-1-guide',
      title: '7v7 Soccer Formation Guide: 2-3-1 Explained with Movement Diagrams',
      blurb: 'full-team shape, roles, and build-up patterns.',
    },
    {
      slug: 'soccer-defender-drills-free-youtube-videos',
      title: 'Soccer Defender Drills + Free YouTube Training Videos',
      blurb: '1v1 defending, tackling, and recovery runs.',
    },
    {
      slug: 'soccer-goalkeeper-drills-free-youtube-videos',
      title: 'Soccer Goalkeeper Drills + Free YouTube Training Videos',
      blurb: 'how your keeper organizes the back line.',
    },
    {
      slug: 'columbus-youth-soccer-planning-kids-3-13-professional-pathway',
      title: 'Columbus Youth Soccer Planning Guide',
      blurb: 'season prep for rec and club families.',
    },
  ],
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Guide overview',
      items: [
        { value: '2 backs', label: 'Center backs in 7v7' },
        { value: '5 diagrams', label: 'Shape & pressing' },
        { value: '3 animated', label: 'Movement clips' },
        { value: '6 videos', label: 'Free on YouTube' },
      ],
    },
    {
      type: 'panel',
      title: 'What this guide covers',
      paragraphs: [
        'In **7v7 soccer** (2-3-1 or similar), your two defenders are really **split center backs**. When the other team has the ball, their job is not to win every tackle—it is to **protect the goal**, **stay compact**, and **delay** until midfielders recover.',
        'This guide focuses on **out-of-possession** work: where to stand, when to step and press, when to drop, and how to slide as a pair when the ball moves wide. Diagrams use **goal on the left**; orange markers are opponents; dashed orange arrows show pressing runs.',
      ],
      chips: ['7v7', 'Defending', 'Pressing', 'Youth & rec'],
    },
    {
      type: 'formationDiagram',
      title: 'Diagram 1 — Base defensive block (they have the ball in midfield)',
      caption:
        'Both defenders sit goal-side of the ball, roughly 8–12 yards apart, in front of the penalty area. Do not chase into midfield—let the striker and center mid press first.',
      legend: DIAGRAM_LEGEND,
      players: [
        { label: 'GK', roleType: 'GK', x: 8, y: 32.5 },
        { label: 'LB', roleType: 'DEF', x: 22, y: 24 },
        { label: 'RB', roleType: 'DEF', x: 22, y: 41 },
        { label: 'CM', roleType: 'MID', x: 34, y: 32.5 },
        { label: 'ST', roleType: 'FWD', x: 42, y: 32.5 },
      ],
      opponents: [
        { label: 'O1', x: 52, y: 32.5 },
        { label: 'O2', x: 58, y: 18 },
        { label: 'O3', x: 58, y: 47 },
      ],
      arrows: [],
    },
    {
      type: 'panel',
      title: 'Defender rules every 7v7 coach should teach',
      list: [
        '**Goal-side first:** always stay between the ball and your goal; never get drawn past the attacker you are marking.',
        '**See ball and player:** open your body so you can see the dribbler and the runner behind you.',
        '**One steps, one covers:** nearest defender pressures; partner tucks central and talks—“I’ve got ball!” / “I’m cover!”',
        '**Delay, don’t dive:** jockey at arm’s length; tackle only when you are sure to win the ball or force a bad touch.',
        '**Communicate with the keeper:** “Step!” “Drop!” “Keeper on!”—the goalkeeper is your eyes when the ball is over your shoulder.',
      ],
    },
    {
      type: 'formationDiagram',
      title: 'Diagram 2 — Pressing the ball carrier (animated)',
      caption:
        'When the striker forces a heavy touch or a back pass, the near defender steps up to press. The other defender drops central to cover the through ball. Watch the blue LB marker slide forward on the loop.',
      legend: DIAGRAM_LEGEND,
      animated: true,
      players: [
        { label: 'GK', roleType: 'GK', x: 8, y: 32.5 },
        {
          label: 'LB',
          roleType: 'DEF',
          x: 34,
          y: 28,
          animateFrom: { x: 22, y: 24 },
        },
        {
          label: 'RB',
          roleType: 'DEF',
          x: 20,
          y: 36,
          animateFrom: { x: 22, y: 41 },
        },
        { label: 'CM', roleType: 'MID', x: 38, y: 32.5 },
        { label: 'ST', roleType: 'FWD', x: 46, y: 32.5 },
      ],
      opponents: [
        { label: 'O1', x: 40, y: 30 },
        { label: 'O2', x: 56, y: 20 },
      ],
      arrows: [
        { from: { x: 46, y: 32.5 }, to: { x: 40, y: 30 }, style: 'run', label: 'ST press', pulse: true },
        { from: { x: 34, y: 28 }, to: { x: 40, y: 30 }, style: 'run', label: 'Step', pulse: true },
        { from: { x: 20, y: 36 }, to: { x: 28, y: 34 }, style: 'run', label: 'Cover' },
      ],
    },
    {
      type: 'panel',
      title: 'When should defenders press in 7v7?',
      paragraphs: [
        'Young teams press too early and leave the middle open. Train **triggers**—specific moments when stepping is safe.',
      ],
      list: [
        '**Press:** back pass to a defender under pressure; heavy first touch; attacker facing own goal; wide ball with no central outlet.',
        '**Hold / drop:** attacker facing you with space; pass into feet with a runner behind you; you are the last defender and help is not goal-side.',
        '**Recover:** if your partner is beaten, sprint goal-side—not alongside the attacker.',
      ],
    },
    {
      type: 'table',
      title: 'Press vs. hold — quick decision table',
      headers: ['Situation', 'Near defender', 'Cover defender', 'Midfielder'],
      rows: [
        [
          'Ball central, 30+ yds out',
          'Hold line, delay',
          'Tuck central',
          'Screen pass to feet',
        ],
        [
          'Back pass / bad touch',
          'Step and press',
          'Drop 3–5 yds deeper',
          'Cut return pass',
        ],
        [
          'Ball on your flank',
          'Press wide, show inside',
          'Slide toward ball',
          'Help double if trapped',
        ],
        [
          'Through ball in behind',
          'Sprint goal-side',
          'Hold depth, cover goal',
          'Track runner',
        ],
        [
          'Counter after we lose it',
          'Recover first 2 sec',
          'Stay home',
          'Foul or delay if last man',
        ],
      ],
    },
    {
      type: 'formationDiagram',
      title: 'Diagram 3 — Ball on the wing: slide as a unit (animated)',
      caption:
        'When the attack moves wide, both defenders shift toward the ball side. The far defender tucks in to protect the center—not ball-watching on the weak side. LB and RB markers animate into their shifted positions.',
      legend: DIAGRAM_LEGEND,
      animated: true,
      players: [
        { label: 'GK', roleType: 'GK', x: 8, y: 32.5 },
        {
          label: 'LB',
          roleType: 'DEF',
          x: 26,
          y: 16,
          animateFrom: { x: 22, y: 24 },
        },
        {
          label: 'RB',
          roleType: 'DEF',
          x: 24,
          y: 34,
          animateFrom: { x: 22, y: 41 },
        },
        { label: 'RM', roleType: 'MID', x: 36, y: 48 },
        { label: 'ST', roleType: 'FWD', x: 44, y: 38 },
      ],
      opponents: [
        { label: 'O1', x: 48, y: 52 },
        { label: 'O2', x: 54, y: 44 },
      ],
      arrows: [
        { from: { x: 26, y: 16 }, to: { x: 48, y: 52 }, style: 'run', label: 'Press wide', pulse: true },
        { from: { x: 24, y: 34 }, to: { x: 32, y: 36 }, style: 'run', label: 'Tuck in' },
        { from: { x: 36, y: 48 }, to: { x: 44, y: 50 }, style: 'run', label: 'RM help' },
      ],
    },
    {
      type: 'panel',
      title: 'Wing defending — coaching points',
      list: [
        'Force the dribbler **down the line** (away from goal) when they are wide; show them the touchline.',
        'If they cut inside, your body angle should already funnel them into your covering partner or the CM.',
        'Do not sell yourself on a block tackle near the corner flag—a miss leaves the whole middle open.',
        'When the cross comes, the **far defender** attacks the ball; near defender marks the near-post runner.',
      ],
    },
    {
      type: 'formationDiagram',
      title: 'Diagram 4 — Compact mid-block before a press',
      caption:
        'Ideal spacing when the opponent builds from their back line: midfielders screen central passes, defenders stay connected, striker cuts the easy ball into the CM.',
      legend: DIAGRAM_LEGEND,
      players: [
        { label: 'GK', roleType: 'GK', x: 8, y: 32.5 },
        { label: 'LB', roleType: 'DEF', x: 20, y: 23 },
        { label: 'RB', roleType: 'DEF', x: 20, y: 42 },
        { label: 'LM', roleType: 'MID', x: 32, y: 14 },
        { label: 'CM', roleType: 'MID', x: 30, y: 32.5 },
        { label: 'RM', roleType: 'MID', x: 32, y: 51 },
        { label: 'ST', roleType: 'FWD', x: 40, y: 32.5 },
      ],
      opponents: [
        { label: 'O1', x: 58, y: 32.5 },
        { label: 'O2', x: 64, y: 22 },
        { label: 'O3', x: 64, y: 43 },
        { label: 'O4', x: 72, y: 32.5 },
      ],
      arrows: [
        { from: { x: 40, y: 32.5 }, to: { x: 58, y: 32.5 }, style: 'run', label: 'Curve press' },
        { from: { x: 30, y: 32.5 }, to: { x: 50, y: 32.5 }, style: 'run', label: 'Block lane' },
      ],
    },
    {
      type: 'formationDiagram',
      title: 'Diagram 5 — Recovery after losing a duel (animated)',
      caption:
        'If the near defender is beaten, the partner does not chase the ball—she sprints goal-side to become the last defender. The beaten defender recovers behind. Orange arrow shows the recovery path.',
      legend: DIAGRAM_LEGEND,
      animated: true,
      players: [
        { label: 'GK', roleType: 'GK', x: 8, y: 32.5 },
        { label: 'LB', roleType: 'DEF', x: 38, y: 26, sublabel: 'beaten' },
        {
          label: 'RB',
          roleType: 'DEF',
          x: 18,
          y: 34,
          animateFrom: { x: 24, y: 40 },
        },
      ],
      opponents: [
        { label: 'O1', x: 44, y: 28 },
        { label: 'O2', x: 50, y: 32.5 },
      ],
      arrows: [
        { from: { x: 38, y: 26 }, to: { x: 18, y: 34 }, style: 'run', label: 'Recover', pulse: true },
        { from: { x: 44, y: 28 }, to: { x: 28, y: 32.5 }, style: 'run', label: 'O1 runs' },
      ],
    },
    {
      type: 'panel',
      title: 'Phrases defenders should use out loud',
      list: [
        '**“Step!”** — partner can press because you have cover.',
        '**“Drop!”** — ball over the top or runner in behind; hold the line deeper.',
        '**“Ball!”** — you are closing the dribbler.',
        '**“Away!”** — clear the ball wide or long; keeper or defender heading.',
        '**“Man on!”** — warn a teammate before a rear tackle.',
      ],
    },
    {
      type: 'videoEmbed',
      title: 'Watch: 7v7 defending shape',
      intro:
        'This session from **Soccer Made Easy** shows compact defending and pressing triggers in small-sided games—the same ideas as Diagrams 2 and 4.',
      embeds: [
        {
          youtubeId: 'LzCZ858V5AI',
          title: '7v7 Defending — Soccer Made Easy',
          caption: 'Compact shape, when to step, and how midfielders support the back two.',
        },
      ],
    },
    {
      type: 'videoList',
      title: 'More free YouTube videos for defenders',
      intro: 'Pair diagram study with reps on the field. These channels break down body shape, 1v1 delay, and team defending.',
      videos: [
        {
          title: 'Dominate Defense: Instant Tips for Better Soccer Defending',
          channel: 'Progressive Soccer',
          description: 'Patience when closing down—perfect for teaching “delay, don’t dive.”',
          url: 'https://www.youtube.com/watch?v=hFSmx8BE-uk',
        },
        {
          title: 'How to STOP fast skillful attackers',
          channel: 'Unisport',
          description: 'Angle your body and force play wide without lunging.',
          url: 'https://www.youtube.com/watch?v=K17wDaDcuyY',
        },
        {
          title: 'MASTER 1v1 DEFENDING with these tips!',
          channel: 'Train Effective',
          description: 'Step-by-step approach distance for the defender who steps from the back line.',
          url: 'https://www.youtube.com/watch?v=CFc0V47Nzng',
        },
        {
          title: 'How To Defend 1v1s - 3 Tips From Rio Ferdinand',
          channel: 'Train Effective',
          description: 'Legendary center back on timing, distance, and when not to tackle.',
          url: 'https://www.youtube.com/watch?v=vPg5huwdbp4',
        },
        {
          title: 'Soccer Offside Trap: Defend Like a Pro!',
          channel: 'Progressive Soccer',
          description: 'Line discipline when your league uses offside in 7v7—step up together.',
          url: 'https://www.youtube.com/watch?v=SlLAQ63KoBs',
        },
      ],
    },
    {
      type: 'table',
      title: '60-minute practice plan — defending focus',
      headers: ['Time', 'Activity', 'Coaching focus'],
      rows: [
        ['10 min', 'Mirror shuffle + jockey (pairs)', 'Side-on stance, stay on balls of feet'],
        ['10 min', '2v2 to mini goals in a channel', 'Force wide; cover defender tucks in'],
        ['10 min', 'Slide drill: coach moves ball L/R', 'Defenders shift as in Diagram 3'],
        ['15 min', '7v7 scrimmage — goal only counts if won ball in own half first', 'Press triggers from Diagram 2'],
        ['10 min', 'Recovery sprints: beaten then sprint goal-side', 'Diagram 5 pattern'],
        ['5 min', 'Cool-down + team debrief', 'One thing we did well / one to fix'],
      ],
    },
    {
      type: 'panel',
      title: 'Common 7v7 defender mistakes',
      list: [
        '**Both chase the ball** — leaves the middle empty; one must always hold central cover.',
        '**Standing flat, square to the ball** — cannot see runner; open hips 45°.',
        '**Pressing from too far** — attacker spins you; close distance in the last 3 steps only.',
        '**Booting every clearance** — sometimes a short pass to the CM restarts attack; clear wide only under pressure.',
        '**Ignoring the keeper** — in 7v7 the GK sees the whole field; listen and organize.',
      ],
      callout:
        'Use the Soccer app to set a 2-3-1 lineup and share this guide with parents before the first defensive practice.',
    },
  ],
  referenceLinks: [
    {
      label: 'US Youth Soccer — small-sided games',
      url: 'https://www.usyouthsoccer.org/',
    },
    {
      label: 'U.S. Soccer — player development initiatives',
      url: 'https://www.ussoccer.com/players/player-development/initiatives',
    },
    {
      label: 'Soccer Made Easy — YouTube channel',
      url: 'https://www.youtube.com/@SoccerMadeEasy',
    },
    {
      label: '7v7 formation guide (2-3-1) on Daily Sports Life',
      url: 'https://dailysportslife.com/blog/7v7-soccer-formation-2-3-1-guide',
    },
    {
      label: 'Defender drills + YouTube videos',
      url: 'https://dailysportslife.com/blog/soccer-defender-drills-free-youtube-videos',
    },
  ],
};
